import { Button, Group, Stack, TextInput, useMantineTheme } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { FieldRemoveControl } from 'app/Private/Clients/Form';
import { Panel } from 'components/Panel';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useEffect } from 'react';
import { hasAtLeast, isEmpty, keys, last } from 'remeda';
import type { CreateContactPersonForm } from 'shared/hooks/use-create-contact-person';

type MultiEmailInputProps = {
  form: UseFormReturnType<CreateContactPersonForm>;
};

export const MultiEmailInput = ({ form }: MultiEmailInputProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { emails } = form.values;

  useEffect(() => {
    if (isEmpty(emails)) addEmailInput();
  }, [emails]);

  const addEmailInput = () => {
    form.insertListItem('emails', '');
  };

  const canAddEmail = () => {
    if (!hasAtLeast(emails, 1)) return false;

    const lastEmail = last(emails).trim();

    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;
    return !emailRegex.test(lastEmail);
  };

  const shouldShowError = keys(form.errors).some(key => key.startsWith('emails.'));

  const emailInputFields = emails.map((emailItem, index) => (
    <TextInput
      {...form.getInputProps(`emails.${index}`)}
      key={form.key(`emails.${index}`)}
      flex={1}
      required={index === 0}
      onChange={event => {
        form.setFieldValue(`emails.${index}`, event.currentTarget.value);
      }}
      label={`${t(i18nKeys.EMAIL)} ${index + 1}`}
      placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.EMAIL)}
      rightSection={
        <FieldRemoveControl
          data={emails}
          disabled={isEmpty(emailItem.trim())}
          onClear={() => form.setFieldValue(`emails.${index}`, '')}
          onRemove={() => form.removeListItem('emails', index)}
        />
      }
    />
  ));

  return (
    <Panel
      title={t(i18nKeys.EMAILS)}
      style={{ borderColor: shouldShowError ? theme.colors.error[5] : theme.colors.gray[2] }}
    >
      <Stack gap="sm">
        {emailInputFields}
        <Group justify="end" mt="lg">
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={addEmailInput}
            size="sm"
            disabled={canAddEmail()}
          >
            {t(i18nKeys.FORM.CLIENT.ADD_EMAIL)}
          </Button>
        </Group>
      </Stack>
    </Panel>
  );
};
